<template>
  <div class="container">
    <rapid-access-technical-evaluation-table v-model="localProposals"
                                             :helpers-loading="helpersLoading"
                                             :is-user-editor="isUserEditor"
                                             :proposals-loading="proposalsLoading"
                                             :table-fields="evaluationHelpers.tableFields"
                                             @filtered-proposals="filteredProposals"
                                             @open-submit-confirmation-modal="openConfirmationModal"
                                             @open-rapid-access-evaluation-technical-modal="openEvaluationModal"
                                             @set-selected-proposal="setSelectedProposal"/>

    <cryoem-rapid-access-technical-evaluation-modal v-if="!isSynchrotron"
                                                    ref="rapidAccessTechnicalEvaluationModal"
                                                    :is-evaluation-editable="isEvaluationEditable"
                                                    :proposal-to-edit="selectedProposal"
                                                    :shifts-options="evaluationHelpers.shiftsOptions"
                                                    @save-evaluation="saveEvaluation"/>

    <rapid-access-technical-evaluation-modal v-else
                                             ref="rapidAccessTechnicalEvaluationModal"
                                             :is-evaluation-editable="isEvaluationEditable"
                                             :proposal-details-fields="evaluationHelpers.modalProposalTableDetailsFields"
                                             :proposal-fields="evaluationHelpers.modalProposalTableFields"
                                             :proposal-to-edit="selectedProposal"
                                             @save-evaluation="saveEvaluation"/>

    <base-submit-danger-modal ref="submitConfirmationModal"
                              :confirm-function="submitEvaluation"
                              class="modal-class"
                              confirm-button-title="Confirm"
                              modal-title="Confirm submission">
      <p class="my-1 p-1 text-center">Do you want to submit evaluation of this proposal?</p>
      <p class="p-1 custom-text-danger warning-text">Submitting is final and cannot be revoked.</p>
    </base-submit-danger-modal>
  </div>
</template>

<script>
  import BaseSubmitDangerModal from '@/components/base-components/base-modals/BaseSubmitDangerModal';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import {
    setExperimentColorAndCommentsInProposals,
    setDataInProposals,
  } from '@/helpers/evaluation/rapid-access/rapidAccessTechnicalEvaluation';
  import RapidAccessTechnicalEvaluationTable
    from '@/components/evaluation/rapid-access/technical/RapidAccessTechnicalEvaluationTable';
  import CryoemRapidAccessTechnicalEvaluationModal
    from '@/components/evaluation/rapid-access/technical/CryoemRapidAccessTechnicalEvaluationModal';
  import RapidAccessTechnicalEvaluationModal
    from '@/components/evaluation/rapid-access/technical/RapidAccessTechnicalEvaluationModal';
  import { getId } from '@/helpers';
  import { rapidAccessTechnicalEvaluation as urlLayer } from '@/api/url_layer';

  export default {
    name: 'RapidAccessTechnicalEvaluationTab',
    components: {
      RapidAccessTechnicalEvaluationTable,
      CryoemRapidAccessTechnicalEvaluationModal,
      RapidAccessTechnicalEvaluationModal,
      BaseSubmitDangerModal,
    },
    mixins: [infrastructureMixin],
    props: {
      proposals: {
        type: Array,
        default: () => [],
      },
      proposalsLoading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        evaluationHelpers: {},
        helpersLoading: false,
        isEvaluationEditable: false,
        selectedProposal: {},
      };
    },
    computed: {
      isSynchrotron() {
        return this.infrastructure === 'synchrotron';
      },
      isUserEditor() {
        const permissions = this.$store.getters['login/getLoggedUserAttribute']('permissions') || [];
        return permissions.includes(this.evaluationHelpers.editorPermission);
      },
      localProposals() {
        // temporary change - move setting experiment color to setDataInProposals
        // when changing cryo RA inhouse_grade into technical_evaluation
        const listOfProposals = setExperimentColorAndCommentsInProposals(this.proposals);
        if (this.isSynchrotron) {
          return setDataInProposals(listOfProposals);
        }
        return listOfProposals;
      },
    },
    async created() {
      this.helpersLoading = true;
      const helpers = await this.getRapidAccessEvaluationDashboardProperties(
        this.infrastructure,
        'TechnicalEvaluation',
      );
      // temporary change - to update when changing cryo RA inhouse_grade into technical_evaluation
      const helpersNames = this.isSynchrotron
        ? ['tableFields', 'editorPermission', 'modalProposalTableFields', 'modalProposalTableDetailsFields']
        : ['tableFields', 'editorPermission', 'shiftsOptions'];
      this.evaluationHelpers = helpers.getAsObject(...helpersNames);
      this.helpersLoading = false;
    },
    methods: {
      setSelectedProposal(proposal) {
        this.selectedProposal = proposal;
      },
      filteredProposals(proposals) {
        this.$emit('filtered-proposals', proposals);
      },
      closeConfirmationModal() {
        this.$refs.submitConfirmationModal.hide();
      },
      openConfirmationModal() {
        this.$refs.submitConfirmationModal.show();
      },
      openEvaluationModal(isEvaluationEditable) {
        this.$set(this, 'isEvaluationEditable', isEvaluationEditable);
        this.$refs.rapidAccessTechnicalEvaluationModal.show();
      },
      async saveEvaluation(evaluationData) {
        try {
          const proposalId = getId(this.selectedProposal);
          const response = await urlLayer.saveRapidAccessProposalTechnicalEvaluation(
            proposalId,
            this.infrastructure,
            evaluationData,
          );
          this.setSelectedProposal(response.data.proposal);
          this.$emit('update-proposals', response.data.proposal);
          this.$notify({ type: 'success', title: 'Proposal evaluation saved successfully' });
        } catch (error) {
          const errorMessage = error.response.data.error ? error.response.data.error : error.response.data;
          this.$notify({ type: 'error', title: error.response.data === false ? 'Saving error!' : errorMessage });
        }
      },
      async submitEvaluation() {
        try {
          const proposalId = getId(this.selectedProposal);
          const response = await urlLayer.submitRapidAccessProposalTechnicalEvaluation(
            proposalId,
            this.infrastructure,
          );
          this.setSelectedProposal(response.data.proposal);
          this.$emit('update-proposals', response.data.proposal);
          this.$notify({ type: 'success', title: 'Proposal evaluation submitted successfully' });
        } catch (error) {
          const errorMessage = error.response.data.error ? error.response.data.error : error.response.data;
          this.$notify({ type: 'error', title: error.response.data === false ? 'Saving error!' : errorMessage });
        }
        this.closeConfirmationModal();
      },
    },
  };
</script>

<style scoped>
  .container {
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 0;
    padding: 0;
  }

  .warning-text {
    text-align: center;
  }
</style>
